import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { formatMoney } from '@/lib/credit';
import {
  fetchCreatorEarnings,
  CANDIDATURE_STATUT_META,
  type CreatorEarning,
} from '@/lib/missions';

function formatDate(value: string): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

/**
 * CreatorEarningsTab — « Mes gains » de l'espace client.
 *
 * Affiche les revenus du créateur issus des missions de visibilité :
 * montant total validé (payé), montants en attente de validation, et le
 * détail mission par mission (vues validées, lien de publication).
 */
export default function CreatorEarningsTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<CreatorEarning[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCreatorEarnings(user.id);
      setRows(data);
    } catch {
      setError('Impossible de charger vos gains pour le moment.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // Notifications/missions en temps réel : le montant apparaît dès validation.
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`creator-earnings-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'mission_candidatures', filter: `user_id=eq.${user.id}` },
        () => { void load(); },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [user, load]);

  if (!user) return null;

  const validated = rows.filter((r) => r.statut === 'validee');
  const pending = rows.filter((r) => r.statut === 'acceptee' || r.statut === 'livree');
  // Historique des paiements : seules les publications validées sont réglées.
  const payments = [...validated].sort((a, b) => (a.date < b.date ? 1 : -1));

  const totalsByCurrency = validated.reduce<Record<string, number>>((acc, row) => {
    const key = row.monaie || 'MAD';
    acc[key] = (acc[key] || 0) + row.montantDu;
    return acc;
  }, {});
  const currencies = Object.keys(totalsByCurrency);
  const totalViews = validated.reduce((sum, r) => sum + (Number(r.vuesValidees) || 0), 0);

  return (
    <div className="max-w-4xl xl:max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Mes gains créateur</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Vos revenus issus des campagnes de visibilité : chaque publication validée par le commerce
            est rémunérée selon le nombre de vues retenues.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-semibold text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap self-start"
        >
          <i className="ri-refresh-line"></i>Actualiser
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-700 text-sm rounded-lg">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={load} className="ml-auto underline cursor-pointer">Réessayer</button>
        </div>
      )}

      {/* Total validé */}
      <div className="bg-accent-500 text-background-50 rounded-xl p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <span className="w-12 h-12 rounded-xl bg-background-50/15 flex items-center justify-center flex-shrink-0">
            <i className="ri-money-dollar-circle-line text-2xl"></i>
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-background-50/80">
              Gains cumulés
            </p>
            <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              {currencies.length === 0 ? (
                <span className="text-2xl md:text-3xl font-bold font-heading">{formatMoney(0, 'MAD')}</span>
              ) : (
                currencies.map((cur) => (
                  <span key={cur} className="text-2xl md:text-3xl font-bold font-heading">
                    {formatMoney(totalsByCurrency[cur], cur)}
                  </span>
                ))
              )}
            </div>
            <p className="text-[11px] text-background-50/80 mt-1.5">
              {validated.length} publication{validated.length > 1 ? 's' : ''} validée{validated.length > 1 ? 's' : ''}
              {' · '}
              {totalViews.toLocaleString('fr-FR')} vues retenues
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50/15 font-medium whitespace-nowrap">
            <i className="ri-time-line"></i>
            {pending.length} en attente
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50/15 font-medium whitespace-nowrap">
            <i className="ri-eye-line"></i>
            {totalViews.toLocaleString('fr-FR')} vues
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50/15 font-medium whitespace-nowrap">
            <i className="ri-file-list-3-line"></i>
            {validated.length} paiement{validated.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/40 rounded-xl text-center px-4">
          <div className="w-14 h-14 rounded-2xl bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
            <i className="ri-megaphone-line text-2xl"></i>
          </div>
          <h3 className="text-foreground-800 font-semibold text-sm mb-1">Aucun gain pour le moment</h3>
          <p className="text-foreground-400 text-xs max-w-md">
            Postulez à une campagne de visibilité depuis l&apos;onglet Missions. Dès qu&apos;une publication
            est validée, le montant apparaît ici.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const meta = CANDIDATURE_STATUT_META[row.statut] || {
              label: row.statut,
              className: 'bg-background-200/70 text-foreground-600',
            };
            const views = Number(row.vuesValidees ?? row.vuesDeclarees ?? 0);
            return (
              <div
                key={row.id}
                className="bg-background-50 border border-background-200/40 rounded-xl p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm md:text-base font-bold text-foreground-950 truncate">{row.missionTitre}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.className}`}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-500 mt-1">
                    {formatDate(row.date)}
                    {' · '}
                    {views.toLocaleString('fr-FR')} vues
                    {row.statut === 'validee' && row.vuesValidees != null ? ' retenues' : ' déclarées'}
                  </p>
                  {row.lienPublication && (
                    <a
                      href={row.lienPublication}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-600 hover:text-primary-700 mt-1.5 no-underline"
                    >
                      <i className="ri-external-link-line"></i>
                      Voir ma publication
                    </a>
                  )}
                </div>
                <div className="text-left sm:text-right flex-shrink-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400">Montant</p>
                  <p className={`text-base md:text-lg font-bold font-heading whitespace-nowrap ${
                    row.statut === 'validee' ? 'text-accent-700' : 'text-foreground-400'
                  }`}>
                    {row.statut === 'validee'
                      ? formatMoney(row.montantDu, row.monaie)
                      : row.statut === 'livree'
                        ? 'En vérification'
                        : '—'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-start gap-2.5 bg-background-100/60 border border-background-200/50 rounded-xl p-4">
        <i className="ri-information-line text-secondary-600 mt-0.5"></i>
        <p className="text-xs text-foreground-500 leading-relaxed">
          Le montant d&apos;une mission est calculé ainsi : <strong className="text-foreground-700">vues retenues ÷ 1000 × tarif pour 1000 vues</strong>.
          Seules les publications validées par le propriétaire de la campagne sont rémunérées.
        </p>
      </div>

      {/* Historique des paiements reçus : chaque publication validée = un règlement. */}
      <section className="bg-background-50 border border-background-200/40 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3.5 border-b border-background-200/40">
          <div>
            <h3 className="text-sm font-bold font-heading text-foreground-950 flex items-center gap-2">
              <i className="ri-wallet-3-line text-accent-600"></i>
              Historique des paiements reçus
            </h3>
            <p className="text-[11px] text-foreground-500 mt-0.5">
              Chaque publication validée par le commerce donne lieu à un paiement.
            </p>
          </div>
          <span className="text-[11px] font-semibold text-foreground-500 whitespace-nowrap">
            {payments.length} règlement{payments.length > 1 ? 's' : ''}
          </span>
        </div>

        {payments.length === 0 ? (
          <div className="px-4 md:px-5 py-8 text-center">
            <p className="text-xs text-foreground-400">
              Aucun paiement pour le moment. Les montants validés apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-background-200/40">
            {payments.map((p) => (
              <div key={`pay-${p.id}`} className="flex items-center gap-3 px-4 md:px-5 py-3">
                <span className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900 truncate">{p.missionTitre}</p>
                  <p className="text-[11px] text-foreground-500">
                    {formatDate(p.date)} · {(Number(p.vuesValidees) || 0).toLocaleString('fr-FR')} vues retenues
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-accent-700 whitespace-nowrap">
                    +{formatMoney(p.montantDu, p.monaie)}
                  </p>
                  <p className="text-[10px] text-foreground-400">Payé</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}